import React, { useEffect, useRef, useState } from 'react';
import { Outlet, Link, useLocation } from 'react-router-dom';
import { useTranslation } from './context/LanguageContext';
import {
  Tags,
  LayoutDashboard,
  ShoppingCart,
  Users,
  CheckSquare,
  Truck,
  Wallet,
  User,
  MapPin,
  Menu,
  Moon,
  Sun,
  LogOut,
  Clock,
  Package,
  Warehouse,
  BarChart3,
  DollarSign,
  CalendarCheck,
  Search,
  FileText,
  Store,
  Settings,
  ClipboardList,
  ChevronDown,
  Video,
} from 'lucide-react';
import { useAuth } from './context/AuthContext';
import { useTheme } from './components/theme-provider';
import NotificationsDropdown from './components/NotificationsDropdown';
import { useQuery } from '@tanstack/react-query';
import api from './api/axios';
import { getMediaUrl } from './utils/url';
import ErrorBoundary from './components/ErrorBoundary';

interface Setting {
  key: string;
  value: string;
}

interface NavItem {
  name: string;
  icon: React.ReactNode;
  path: string;
}

const STABLE_EMPTY_SETTINGS: Setting[] = [];

export const DashboardLayout = () => {
  const { user, logout, updateUser } = useAuth();
  const { theme, setTheme } = useTheme();
  const [isMobile, setMobile] = useState(() => window.innerWidth < 768);
  const [isSidebarOpen, setSidebarOpen] = useState(() => window.innerWidth >= 768);
  const menuButton = useRef<HTMLButtonElement>(null);

  useEffect(() => {
    const media = window.matchMedia('(max-width: 767px)');
    const resize = () => {
      setMobile(media.matches);
      setSidebarOpen(!media.matches);
    };
    media.addEventListener('change', resize);
    return () => media.removeEventListener('change', resize);
  }, []);

  useEffect(() => {
    if (!isMobile || !isSidebarOpen) return;
    const close = (event: KeyboardEvent) => {
      if (event.key === 'Escape') {
        setSidebarOpen(false);
        menuButton.current?.focus();
      }
    };
    window.addEventListener('keydown', close);
    return () => window.removeEventListener('keydown', close);
  }, [isMobile, isSidebarOpen]);
  const [isStorefrontOpen, setIsStorefrontOpen] = useState(false);
  const location = useLocation();
  const { t } = useTranslation();

  // Fetch settings for dynamic title
  const { data: settings } = useQuery<Setting[]>({
    queryKey: ['system-settings'],
    queryFn: async () => {
      try {
        const res = await api.get('/settings');
        return Array.isArray(res.data) ? res.data : [];
      } catch (err: unknown) {
        console.warn('System settings fetch failed or access denied:', (err as { message?: string })?.message);
        return [];
      }
    },
    staleTime: 60000,
    retry: false,
    initialData: STABLE_EMPTY_SETTINGS,
  });

  const companyName = String(
    Array.isArray(settings) && settings.length > 0
      ? settings.find((s: Setting) => s.key === 'COMPANY_NAME')?.value ||
          localStorage.getItem('COMPANY_NAME') ||
          'ERP Uzbekistan'
      : localStorage.getItem('COMPANY_NAME') || 'ERP Uzbekistan',
  );

  const isDirector = user?.role === 'DIRECTOR';
  const isAdmin = user?.role === 'ADMIN' || user?.role === 'DIRECTOR';
  const isWarehouseManager = user?.role === 'WAREHOUSE_MANAGER';
  const isRegionalManager = user?.role === 'REGIONAL_MANAGER';
  const isBranchAccountant = user?.role === 'ACCOUNTANT';
  const isAccountant =
    user?.role === 'CHIEF_ACCOUNTANT' ||
    user?.role === 'ACCOUNTANT' ||
    user?.role === 'CASHIER' ||
    user?.login === 'glavbux';

  async function toggleTheme() {
    const newTheme = theme === 'dark' ? 'light' : 'dark';
    setTheme(newTheme);
    try {
      await api.put('/users/profile', { theme: newTheme });
      updateUser({ theme: newTheme });
    } catch {
      // Не критично — тема применена локально, просто не сохранится в БД
      console.warn('Failed to save theme to server');
    }
  }

  const navItems = [
    {
      name: t('nav.panel'),
      icon: <LayoutDashboard size={20} className="text-orange-500" />,
      path: isDirector ? '/director/dashboard' : isAccountant ? '/accountant/dashboard' : '/dashboard',
    },
    { name: t('nav.orders'), icon: <ShoppingCart size={20} className="text-blue-500" />, path: '/orders' },
    {
      name: 'Клиенты',
      icon: <Users size={20} className="text-emerald-500" />,
      path: '/clients',
    },
    ...(isAccountant
      ? []
      : [{ name: t('nav.visits'), icon: <MapPin size={20} className="text-indigo-600" />, path: '/visits' }]),
    { name: t('nav.tasks'), icon: <CheckSquare size={20} className="text-indigo-500" />, path: '/tasks' },
    ...(isAccountant
      ? []
      : [{ name: t('nav.logistics'), icon: <Truck size={20} className="text-sky-500" />, path: '/logistics' }]),
    ...(isAdmin ? [{ name: 'Магазины', icon: <Store size={20} className="text-pink-500" />, path: '/stores' }] : []),
    { name: 'Видеоакция', icon: <Video size={20} className="text-fuchsia-500" />, path: '/video-campaign' },
    { name: 'Документооборот', icon: <FileText size={20} className="text-orange-500" />, path: '/contracts' },
  ];

  const inventoryItems = [
    { name: t('nav.products'), icon: <Package size={20} className="text-amber-600" />, path: '/inventory/products' },
    ...(isAdmin || isAccountant
      ? [
          {
            name: t('nav.prices'),
            icon: <DollarSign size={20} className="text-emerald-500" />,
            path: '/inventory/pricing',
          },
        ]
      : []),
  ];

  const financeItems = [
    { name: t('nav.finance'), icon: <Wallet size={20} className="text-green-500" />, path: '/finance' },
    { name: t('nav.installment'), icon: <Clock size={20} className="text-blue-500" />, path: '/finance/installments' },
  ];

  const hrItems = [
    ...(isAccountant
      ? []
      : [
          { name: t('nav.employees'), icon: <Users size={20} className="text-purple-500" />, path: '/hr/employees' },
          {
            name: t('nav.attendance'),
            icon: <CalendarCheck size={20} className="text-violet-500" />,
            path: '/hr/attendance',
          },
        ]),
  ];

  const warehouseItems = [
    {
      name: 'Склад',
      icon: <Warehouse size={20} className="text-emerald-500" />,
      path: '/warehouse',
    },
  ];

  const analyticsItems = [
    ...(isAdmin
      ? [{ name: t('nav.analytics'), icon: <BarChart3 size={20} className="text-purple-500" />, path: '/analytics' }]
      : []),
  ];

  const referenceItems = [
    { name: t('nav.references'), icon: <Tags size={20} className="text-cyan-500" />, path: '/reference' },
  ];

  const settingsItems = [
    {
      name: t('nav.system_settings'),
      icon: <Settings size={20} className="text-indigo-600" />,
      path: '/settings/system',
    },
    {
      name: 'Журнал действий',
      icon: <ClipboardList size={20} className="text-indigo-600" />,
      path: '/settings/audit-logs',
    },
  ];

  const storefrontItems = [
    { name: 'Дашборд', icon: <LayoutDashboard size={20} className="text-pink-500" />, path: '/storefront/dashboard' },
    { name: 'Заказы', icon: <ShoppingCart size={20} className="text-blue-500" />, path: '/storefront/orders' },
    { name: 'Главная страница', icon: <Store size={20} className="text-orange-500" />, path: '/storefront/home' },
    { name: 'Категории', icon: <Tags size={20} className="text-emerald-500" />, path: '/storefront/categories' },
    { name: 'Карточка товара', icon: <Package size={20} className="text-amber-500" />, path: '/storefront/product' },
    { name: 'Страницы', icon: <FileText size={20} className="text-indigo-500" />, path: '/storefront/pages' },
    { name: 'Блог', icon: <FileText size={20} className="text-cyan-500" />, path: '/storefront/blog' },
    { name: 'SEO', icon: <Search size={20} className="text-violet-500" />, path: '/storefront/seo' },
    { name: 'Настройки', icon: <Settings size={20} className="text-gray-500" />, path: '/storefront/settings' },
  ];

  const warehouseManagerItems = [
    {
      name: t('nav.dashboard'),
      icon: <LayoutDashboard size={20} className="text-orange-500" />,
      path: '/warehouse/dashboard',
    },
    { name: 'Клиенты', icon: <Users size={20} className="text-blue-500" />, path: '/clients' },
    { name: t('nav.orders'), icon: <ShoppingCart size={20} className="text-blue-500" />, path: '/orders' },
    { name: t('nav.visits'), icon: <MapPin size={20} className="text-indigo-600" />, path: '/visits' },
    {
      name: 'Склад',
      icon: <Warehouse size={20} className="text-emerald-500" />,
      path: '/warehouse',
    },
    { name: t('nav.tasks'), icon: <CheckSquare size={20} className="text-indigo-500" />, path: '/tasks' },
    { name: 'Документооборот', icon: <FileText size={20} className="text-orange-500" />, path: '/contracts' },
  ];

  const regionalManagerItems = [
    { name: t('nav.dashboard'), icon: <LayoutDashboard size={20} className="text-orange-500" />, path: '/dashboard' },
    { name: t('nav.orders'), icon: <ShoppingCart size={20} className="text-blue-500" />, path: '/orders' },
    { name: 'Клиенты', icon: <Users size={20} className="text-emerald-500" />, path: '/clients' },
    { name: t('nav.visits'), icon: <MapPin size={20} className="text-indigo-600" />, path: '/visits' },
    {
      name: 'Склад',
      icon: <Warehouse size={20} className="text-emerald-500" />,
      path: '/warehouse',
    },
    { name: t('nav.prices'), icon: <DollarSign size={20} className="text-emerald-500" />, path: '/inventory/pricing' },
    {
      name: t('nav.attendance'),
      icon: <CalendarCheck size={20} className="text-violet-500" />,
      path: '/hr/attendance',
    },
    {
      name: t('nav.kpi_agents'),
      icon: <BarChart3 size={20} className="text-indigo-500" />,
      path: '/analytics/set-plans',
    },
    { name: t('nav.finance'), icon: <Wallet size={20} className="text-green-500" />, path: '/finance' },
    { name: t('nav.installment'), icon: <Clock size={20} className="text-blue-500" />, path: '/finance/installments' },
    { name: t('nav.employees'), icon: <Users size={20} className="text-purple-500" />, path: '/hr/employees' },
    { name: t('nav.tasks'), icon: <CheckSquare size={20} className="text-indigo-500" />, path: '/tasks' },
    { name: t('nav.analytics'), icon: <BarChart3 size={20} className="text-purple-500" />, path: '/analytics' },
    { name: 'Магазины', icon: <Store size={20} className="text-pink-500" />, path: '/stores' },
    { name: 'Документооборот', icon: <FileText size={20} className="text-orange-500" />, path: '/contracts' },
    { name: 'Видеоакция', icon: <Video size={20} className="text-fuchsia-500" />, path: '/video-campaign' },
  ];

  const directorItems = [
    { name: 'Дашборд', icon: <LayoutDashboard size={20} className="text-orange-500" />, path: '/director/dashboard' },
    { name: t('nav.orders'), icon: <ShoppingCart size={20} className="text-blue-500" />, path: '/orders' },
    {
      name: 'Клиенты',
      icon: <Users size={20} className="text-emerald-500" />,
      path: '/clients',
    },
    { name: t('nav.products'), icon: <Package size={20} className="text-amber-600" />, path: '/inventory/products' },
    {
      name: 'Склад',
      icon: <Warehouse size={20} className="text-emerald-500" />,
      path: '/warehouse',
    },
    { name: t('nav.prices'), icon: <DollarSign size={20} className="text-emerald-500" />, path: '/inventory/pricing' },
    { name: t('nav.finance'), icon: <Wallet size={20} className="text-green-500" />, path: '/finance' },
    { name: t('nav.installment'), icon: <Clock size={20} className="text-blue-500" />, path: '/finance/installments' },
    { name: t('nav.employees'), icon: <Users size={20} className="text-purple-500" />, path: '/hr/employees' },
    { name: t('nav.analytics'), icon: <BarChart3 size={20} className="text-purple-500" />, path: '/analytics' },
    { name: t('nav.kpi'), icon: <BarChart3 size={20} className="text-indigo-500" />, path: '/analytics/kpi' },
    { name: t('nav.references'), icon: <Tags size={20} className="text-cyan-500" />, path: '/reference' },
    { name: t('nav.locations'), icon: <Warehouse size={20} className="text-blue-600" />, path: '/inventory/locations' },
    { name: 'Магазины', icon: <Store size={20} className="text-pink-500" />, path: '/stores' },
    { name: 'Документооборот', icon: <FileText size={20} className="text-orange-500" />, path: '/contracts' },
    { name: 'Видеоакция', icon: <Video size={20} className="text-fuchsia-500" />, path: '/video-campaign' },
  ];

  const isActive = (path: string) => {
    if (path === '/') return location.pathname === '/' || location.pathname === '/dashboard';
    if (path === '/finance') return location.pathname === '/finance';
    return location.pathname.startsWith(path);
  };

  const renderNavSection = (items: NavItem[], _label?: string) => (
    <>
      {(() => {
        try {
          return items.map((item) => {
            if (!item || typeof item.path !== 'string') {
              console.error('Invalid nav item detected:', item);
              return null;
            }
            return (
              <Link
                key={item.name}
                to={item.path}
                className={`flex items-center gap-3 px-3 py-2.5 rounded-sm transition-colors group ${isActive(item.path) ? 'bg-primary/10 text-primary' : 'hover:bg-primary/5'}`}
              >
                <span
                  className={
                    isActive(item.path)
                      ? ''
                      : 'filter grayscale opacity-70 group-hover:grayscale-0 group-hover:opacity-100 transition-all'
                  }
                >
                  {item.icon}
                </span>
                {isSidebarOpen && <span className="font-medium whitespace-nowrap text-sm">{String(item.name)}</span>}
              </Link>
            );
          });
        } catch (err) {
          console.error('Failed to render nav section:', err);
          return <div className="text-[10px] text-rose-500 p-2">{t('header.nav_error')}</div>;
        }
      })()}
    </>
  );

  return (
    <div className="flex bg-background text-foreground h-screen overflow-hidden font-sans">
      {/* On phones the menu overlays the page instead of consuming its width. */}
      {isMobile && isSidebarOpen && (
        <button
          type="button"
          aria-label={t('layout.close_menu')}
          className="fixed inset-0 z-[60] bg-black/40"
          onClick={() => {
            setSidebarOpen(false);
            menuButton.current?.focus();
          }}
        />
      )}
      <aside
        id="dashboard-navigation"
        inert={isMobile && !isSidebarOpen}
        aria-hidden={isMobile && !isSidebarOpen}
        className={`fixed inset-y-0 left-0 z-[70] flex w-64 max-w-[calc(100vw-3rem)] flex-col border-r border-border bg-background shadow-xs transition-all duration-200 md:relative md:shrink-0 ${isSidebarOpen ? 'translate-x-0 md:w-64' : '-translate-x-full md:translate-x-0 md:w-20'}`}
      >
        <div className="flex h-16 items-center justify-between px-4 mt-2">
          {isSidebarOpen && (
            <span className="font-bold text-lg text-primary drop-shadow-xs truncate pr-2">{companyName}</span>
          )}
          <button
            onClick={() => setSidebarOpen(!isSidebarOpen)}
            aria-label={t(isSidebarOpen ? 'layout.close_menu' : 'layout.open_menu')}
            className="p-2 rounded-sm hover:bg-primary/10 transition-colors"
          >
            <Menu className="w-5 h-5 text-muted-foreground hover:text-primary" />
          </button>
        </div>

        <nav
          onClick={(event) => {
            if (isMobile && (event.target as HTMLElement).closest('a')) setSidebarOpen(false);
          }}
          className="flex-1 mt-4 px-3 overflow-y-auto override-scrollbars space-y-0.5 pb-10"
        >
          {isWarehouseManager ? (
            renderNavSection(warehouseManagerItems)
          ) : isRegionalManager ? (
            renderNavSection(regionalManagerItems)
          ) : isDirector ? (
            renderNavSection(directorItems)
          ) : (
            <>
              {renderNavSection(navItems)}
              {renderNavSection(inventoryItems, 'Инвентарь')}
              {(isAdmin || isAccountant) && (
                <>
                  {renderNavSection(financeItems, 'Финансы')}
                  {hrItems.length > 0 && renderNavSection(hrItems, 'Кадры')}
                  {renderNavSection(warehouseItems, 'Склад')}
                  {analyticsItems.length > 0 && renderNavSection(analyticsItems, 'Аналитика')}
                  {!isBranchAccountant && renderNavSection(referenceItems, 'Справочники')}
                  {isAdmin && (
                    <div className="mt-4 pt-2 border-t border-border/50">
                      <button
                        onClick={() => setIsStorefrontOpen(!isStorefrontOpen)}
                        className="w-full flex items-center justify-between px-3 py-2 text-xs font-bold uppercase tracking-wider text-muted-foreground hover:text-foreground transition-colors"
                      >
                        <div className="flex items-center gap-2">
                          <Store size={14} className="text-orange-500" />
                          {isSidebarOpen && <span>Интернет-магазин</span>}
                        </div>
                        {isSidebarOpen && (
                          <ChevronDown
                            size={14}
                            className={`transition-transform duration-200 ${isStorefrontOpen ? 'rotate-180' : ''}`}
                          />
                        )}
                      </button>
                      <div
                        className={`overflow-hidden transition-all duration-300 ${isStorefrontOpen ? 'max-h-[500px] opacity-100' : 'max-h-0 opacity-0'}`}
                      >
                        <div className="mt-1 space-y-0.5 ml-2 border-l-2 border-border/50 pl-2">
                          {renderNavSection(storefrontItems)}
                        </div>
                      </div>
                    </div>
                  )}
                  {isAdmin && renderNavSection(settingsItems, 'Настройки')}
                </>
              )}
            </>
          )}
        </nav>
      </aside>

      {/* Main Container */}
      <div inert={isMobile && isSidebarOpen} className="min-w-0 flex-1 flex flex-col h-screen overflow-hidden">
        {/* Header */}
        <header className="h-16 shrink-0 flex items-center justify-between gap-2 px-3 sm:px-6 border-b border-border bg-background/80 backdrop-blur-md sticky top-0 z-50 shadow-xs">
          <div className="flex min-w-0 items-center gap-2 sm:gap-4">
            <button
              ref={menuButton}
              type="button"
              aria-label={t('layout.open_menu')}
              aria-controls="dashboard-navigation"
              aria-expanded={isSidebarOpen}
              onClick={() => setSidebarOpen(true)}
              className="md:hidden shrink-0 p-2 rounded-sm hover:bg-primary/10"
            >
              <Menu size={20} />
            </button>
            <h1 className="min-w-0 truncate text-lg font-bold tracking-tight text-neutral-800 dark:text-neutral-100">
              {(() => {
                try {
                  return (
                    (isWarehouseManager && warehouseManagerItems.find((item) => isActive(item.path))?.name) ||
                    (isRegionalManager && regionalManagerItems.find((item) => isActive(item.path))?.name) ||
                    navItems.find((item) => isActive(item.path))?.name ||
                    inventoryItems.find((item) => isActive(item.path))?.name ||
                    financeItems.find((item) => isActive(item.path))?.name ||
                    hrItems.find((item) => isActive(item.path))?.name ||
                    warehouseItems.find((item) => isActive(item.path))?.name ||
                    analyticsItems.find((item) => isActive(item.path))?.name ||
                    referenceItems.find((item) => isActive(item.path))?.name ||
                    settingsItems.find((item) => isActive(item.path))?.name ||
                    storefrontItems.find((item) => isActive(item.path))?.name ||
                    'Панель управления'
                  );
                } catch (e) {
                  console.error('Title derivation failed:', e);
                  return localStorage.getItem('COMPANY_NAME') || 'ERP Uzbekistan';
                }
              })()}
            </h1>
          </div>

          <div className="flex shrink-0 items-center gap-1 sm:gap-4">
            {/* Search trigger for Ctrl+K */}
            <div
              className="hidden xl:flex relative items-center cursor-text group"
              onClick={() => document.dispatchEvent(new CustomEvent('open-command-palette'))}
            >
              <Search className="absolute left-3 w-4 h-4 text-muted-foreground group-hover:text-foreground transition-colors" />
              <input
                type="text"
                placeholder={t('header.search')}
                readOnly
                className="w-64 pl-9 pr-12 py-1.5 text-sm bg-muted/50 border border-border rounded-sm cursor-text hover:bg-muted transition-colors outline-hidden text-muted-foreground placeholder:text-muted-foreground pointer-events-none"
              />
              <div className="absolute right-2 flex items-center">
                <kbd className="text-[10px] font-mono font-semibold bg-background border border-border px-1.5 py-0.5 rounded text-muted-foreground">
                  ⌘K
                </kbd>
              </div>
            </div>
            <NotificationsDropdown />
            <button
              onClick={toggleTheme}
              className="p-2 rounded-full hover:bg-primary/10 transition-colors text-muted-foreground group"
              title={t('header.toggle_theme')}
            >
              {theme === 'dark' ? (
                <Sun className="w-5 h-5 group-hover:text-amber-500 transition-colors" />
              ) : (
                <Moon className="w-5 h-5 group-hover:text-indigo-500 transition-colors" />
              )}
            </button>
            <div className="h-6 w-px bg-border mx-1" />
            <button
              onClick={logout}
              className="p-2 rounded-full hover:bg-red-500/10 transition-colors text-muted-foreground group"
              title={t('header.logout')}
            >
              <LogOut className="w-5 h-5 group-hover:text-red-500 transition-colors" />
            </button>
            <Link to="/settings/profile" className="flex items-center gap-2 group sm:ml-2">
              <div className="w-9 h-9 rounded-full bg-linear-to-tr from-blue-500 to-indigo-600 flex items-center justify-center text-white shadow-md group-hover:ring-2 ring-blue-500/50 transition-all overflow-hidden">
                {user?.photo_url ? (
                  <img src={getMediaUrl(user.photo_url)} className="w-full h-full object-cover" alt={user.name} />
                ) : (
                  <User className="w-5 h-5" />
                )}
              </div>
              {isSidebarOpen && (
                <div className="hidden lg:block">
                  <p className="text-xs font-bold text-neutral-900 dark:text-neutral-100 leading-none">
                    {user?.name || 'Профиль'}
                  </p>
                  <p className="text-[10px] text-muted-foreground mt-0.5 leading-none">
                    {t(`role.${user?.role || ''}`) || user?.role || t('role.employee')}
                  </p>
                </div>
              )}
            </Link>
          </div>
        </header>

        {/* Content Area */}
        <main className="flex-1 overflow-auto p-6 bg-slate-50 dark:bg-[#020617]">
          <ErrorBoundary>
            <Outlet />
          </ErrorBoundary>
        </main>
      </div>
    </div>
  );
};
